import { APP_NAME } from '../config/brand';
// Shared opener for protected document exports (/api/pdf/*, /api/tally/*, …).
//
// A bare window.open()/location.href sends only the SSO cookie, which is
// domain-scoped (COOKIE_DOMAIN) — Bearer-only sessions (localStorage JWT) get a
// silent 401 tab. This fetches with BOTH credentials and the Authorization
// header, opens the tab synchronously inside the click gesture (popup-safe),
// and surfaces server errors in the tab instead of raw JSON.
//
// Modes:
//   openProtectedExport(url)                          → view in new tab
//   openProtectedExport(url, { print: true })         → new tab + auto print dialog
//   openProtectedExport(url, { download: 'x.pdf' })   → download with filename
//
// The tab is opened blank before the first await (browsers only honour
// window.open inside the click gesture), so for the whole fetch it used to sit
// there as an untitled white void — on a big BMR pack that reads as a hang.
// It now paints a titled loading screen immediately and swaps to the PDF with
// location.replace(), which keeps the spinner out of the tab's history so Back
// doesn't land on a dead page.

const FETCH_TIMEOUT_MS = 60_000;
const REVOKE_POLL_MS   = 5_000;
const REVOKE_MAX_MS    = 30 * 60_000;

// Most callers pass `${API}/api/...`, but a few passed a bare '/api/...'. When
// the app is served from a host that doesn't proxy /api, that
// relative path hits the SPA catch-all, which answers 200 with index.html — so
// res.ok passes, the shell gets blobbed as if it were the document, and the user
// gets a blank tab titled with the app name and no error anywhere. Resolving here
// means a caller can't reintroduce it.
const API_BASE = process.env.REACT_APP_API_URL || '';
function resolveUrl(u) {
  const s = String(u || '');
  if (/^(https?:|blob:|data:)/i.test(s)) return s;
  return `${API_BASE}${s.startsWith('/') ? '' : '/'}${s}`;
}

// The same SPA-shell response can also come back from a genuinely missing route.
// Some exports legitimately return HTML (invoice preview, courier label), so we
// can't reject text/html outright — detect the shell itself instead.
function looksLikeAppShell(text) {
  return /<div\s+id=["']root["']/i.test(text) || new RegExp(`<title>\\s*${APP_NAME}\\s*</title>`, 'i').test(text);
}

const SHELL_CSS = `
  body{margin:0;height:100vh;display:flex;flex-direction:column;align-items:center;
    justify-content:center;gap:14px;background:#faf8f5;color:#3a3226;
    font:14px/1.5 -apple-system,"Segoe UI",Arial,sans-serif}
  .spin{width:26px;height:26px;border:2.5px solid #e3dbd0;border-top-color:#6b2737;
    border-radius:50%;animation:r .7s linear infinite}
  @keyframes r{to{transform:rotate(360deg)}}
  .sub{font-size:12px;color:#8a7f70}
  .err{max-width:30rem;padding:0 24px;text-align:center;white-space:pre-wrap;color:#a4262c}
`;

// Filename for the popup-blocked download fallback: prefer what the server
// asked for, else the last meaningful URL segment, else a generic name.
function filenameFrom(res, url) {
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (m) { try { return decodeURIComponent(m[1]); } catch { return m[1]; } }
  const seg = url.split('?')[0].split('/').filter(Boolean).pop();
  return seg && seg !== 'download' ? `${seg}.pdf` : 'document.pdf';
}

// Human label for the loading screen — "kld bulk" reads better than the raw URL.
function labelFrom(url) {
  const seg = url.split('?')[0].split('/').filter(Boolean).pop() || 'document';
  return seg.replace(/[-_]/g, ' ');
}

// Built with DOM APIs, never an HTML string: `msg` carries server-supplied text
// and a document.write template would make that an injection sink.
function paint(win, title, nodes) {
  try {
    const d = win.document;
    if (!d?.body) return false;
    d.title = title;
    d.body.replaceChildren();
    const style = d.createElement('style');
    style.textContent = SHELL_CSS;
    d.head.appendChild(style);
    for (const [cls, text] of nodes) {
      const el = d.createElement('div');
      if (cls) el.className = cls;
      if (text != null) el.textContent = text;   // escapes by construction
      d.body.appendChild(el);
    }
    return true;
  } catch { return false; }
}

const paintLoading = (win, url) => paint(win, 'Preparing document…', [
  ['spin', null],
  ['', `Preparing ${labelFrom(url)}…`],
  ['sub', "This tab will open the document when it's ready."],
]);

const paintError = (win, msg) => paint(win, 'Export failed', [['err', msg]]);

function saveBlob(blobUrl, filename) {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Blob URLs leak until revoked, but revoking while the tab is still open breaks
// reload and the viewer's own Save button. Release once the tab is gone.
function revokeWhenClosed(blobUrl, win) {
  const started = Date.now();
  const timer = setInterval(() => {
    let closed = true;
    try { closed = win.closed; } catch { closed = false; } // cross-origin once navigated
    if (closed || Date.now() - started > REVOKE_MAX_MS) {
      clearInterval(timer);
      URL.revokeObjectURL(blobUrl);
    }
  }, REVOKE_POLL_MS);
}

export async function openProtectedExport(url, { print = false, download = null, win: openedWin = null } = {}) {
  const target = resolveUrl(url);
  // Downloads never need a tab; views must open it before the first await.
  // A caller that had to await first (the BMR check saves, then prints) opens
  // the tab inside its own click and passes it in as `win`.
  const win = download ? null : (openedWin || window.open('', '_blank'));
  if (win) paintLoading(win, target);

  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);

  try {
    const headers = {};
    const token = localStorage.getItem('aone_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(target, { credentials: 'include', headers, signal: ctrl.signal });

    if (!res.ok) {
      let msg = `Export failed (HTTP ${res.status}).`;
      try {
        const j = await res.json();
        // BMR pre-print check: close the loading tab and let the check dialog
        // (components/BmrPreflightHost) confirm the values, then print again.
        if (res.status === 428 && j?.code === 'BMR_PREFLIGHT_REQUIRED') {
          if (win) win.close();
          window.dispatchEvent(new CustomEvent('aone:bmr-preflight', { detail: { taskId: j.taskId, url, opts: { print, download } } }));
          return false;
        }
        if (j?.error) msg = j.error;
        if (j?.missing?.length) msg += '\n\nMissing fields:\n• ' + j.missing.join('\n• ');
      } catch { /* keep generic message */ }
      // Show the reason where the user is looking. Previously the tab was
      // closed out from under them and an alert fired behind it.
      if (win && paintError(win, msg)) return false;
      if (win) win.close();
      alert(msg);
      return false;
    }

    // A 200 carrying the SPA shell means the request never reached the API
    // (wrong host, or a route that doesn't exist). Blobbing it produces a blank
    // tab with no clue what went wrong — say so instead.
    const ctype = (res.headers.get('content-type') || '').toLowerCase();
    let blob;
    if (ctype.includes('text/html')) {
      const text = await res.text();
      if (looksLikeAppShell(text)) {
        const msg = 'The server returned the app page instead of the document.\n\n'
          + 'This usually means the export address is wrong or the document route is not available.\n\n'
          + target;
        if (win && paintError(win, msg)) return false;
        if (win) win.close();
        alert(msg);
        return false;
      }
      blob = new Blob([text], { type: ctype || 'text/html' });
    } else {
      blob = await res.blob();
    }
    const blobUrl = URL.createObjectURL(blob);

    if (download) {
      saveBlob(blobUrl, download);
      setTimeout(() => URL.revokeObjectURL(blobUrl), REVOKE_POLL_MS);
      return true;
    }

    if (!win) {
      // Popup blocked even for the synchronous open. The old path navigated the
      // CURRENT tab to the blob, throwing away whatever the user was working
      // through. Hand them the file instead — they keep their place.
      saveBlob(blobUrl, filenameFrom(res, target));
      setTimeout(() => URL.revokeObjectURL(blobUrl), REVOKE_POLL_MS);
      return true;
    }

    // replace(), not assign() — the loading screen stays out of tab history.
    win.location.replace(blobUrl);

    if (print) {
      // The PDF plugin's load event is unreliable across browsers, so race it
      // against a timer and let whichever wins fire the dialog exactly once.
      let printed = false;
      const fire = () => {
        if (printed) return;
        printed = true;
        try { win.focus(); win.print(); } catch { /* tab closed or blocked */ }
      };
      try { win.addEventListener('load', fire); } catch { /* cross-origin */ }
      setTimeout(fire, 1500);
    }

    revokeWhenClosed(blobUrl, win);
    return true;
  } catch (e) {
    const msg = e?.name === 'AbortError'
      ? 'The document took too long to generate and the request timed out. Try again, or narrow the range if this is a bulk export.'
      : 'Could not reach the server. Check your connection and try again.';
    if (win && paintError(win, msg)) return false;
    if (win) win.close();
    alert(msg);
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export default openProtectedExport;
